import type { IssuanceCertificate } from "./models/issuance";

export type BucketGranularity = "day" | "week" | "month";

export interface TimeBucket {
  label: string;
  startDate: string;
  endDate: string;
  count: number;
}

export interface GroupCount {
  id: string;
  name: string;
  count: number;
}

/** Determine bucket granularity from the number of days in the range. */
export function granularityForRange(days: number): BucketGranularity {
  if (days <= 90) {
    return "day";
  }
  if (days <= 365) {
    return "week";
  }
  return "month";
}

/**
 * Return the ISO week label (YYYY-Www) for a UTC date.
 * ISO 8601: week 1 is the week containing the first Thursday of the year.
 */
function isoWeekLabel(date: Date): string {
  const d = new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
  );
  // Set to nearest Thursday: current date + 4 - current day number (Mon=1..Sun=7)
  const dayNum = d.getUTCDay() || 7; // Sunday = 7
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(
    ((d.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7
  );
  return `${String(d.getUTCFullYear())}-W${String(weekNo).padStart(2, "0")}`;
}

/** Return the bucket label for a given date and granularity. */
function bucketLabel(date: Date, granularity: BucketGranularity): string {
  const yyyy = String(date.getUTCFullYear());
  const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
  if (granularity === "day") {
    const dd = String(date.getUTCDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }
  if (granularity === "week") {
    return isoWeekLabel(date);
  }
  return `${yyyy}-${mm}`;
}
/** Advance a date to the start of the next bucket. Mutates the date in place. */
function advanceBucket(date: Date, granularity: BucketGranularity): void {
  if (granularity === "day") {
    date.setUTCDate(date.getUTCDate() + 1);
  } else if (granularity === "week") {
    date.setUTCDate(date.getUTCDate() + 7);
  } else {
    date.setUTCMonth(date.getUTCMonth() + 1);
  }
}

/** Snap a date to the start of its bucket period (UTC). */
function snapToBucketStart(date: Date, granularity: BucketGranularity): Date {
  const d = new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
  );
  if (granularity === "week") {
    const dayNum = d.getUTCDay() || 7; // Monday=1 .. Sunday=7
    d.setUTCDate(d.getUTCDate() - (dayNum - 1));
  } else if (granularity === "month") {
    d.setUTCDate(1);
  }
  return d;
}

/**
 * Group certificates into time buckets.
 * Generates all buckets in the [startDate, endDate] range, even if count is 0.
 */
export function bucketByTime(
  certificates: IssuanceCertificate[],
  granularity: BucketGranularity,
  startDate: string,
  endDate: string
): TimeBucket[] {
  // Build a map of label → count from the certificates
  const counts = new Map<string, number>();
  for (const cert of certificates) {
    const d = new Date(cert.validFrom);
    const label = bucketLabel(d, granularity);
    counts.set(label, (counts.get(label) ?? 0) + 1);
  }

  // Generate all bucket labels in the range
  const start = snapToBucketStart(new Date(startDate), granularity);
  const end = new Date(endDate);
  const buckets: TimeBucket[] = [];
  const seen = new Set<string>();

  while (start.getTime() <= end.getTime()) {
    const label = bucketLabel(start, granularity);
    if (!seen.has(label)) {
      seen.add(label);
      const bucketStart = start.toISOString().slice(0, 10);
      const nextStart = new Date(start);
      advanceBucket(nextStart, granularity);
      // End date is the day before the next bucket starts, capped at the range end
      const bucketEndMs = Math.min(
        nextStart.getTime() - 86_400_000,
        end.getTime()
      );
      const bucketEnd = new Date(bucketEndMs).toISOString().slice(0, 10);
      buckets.push({
        label,
        startDate: bucketStart,
        endDate: bucketEnd,
        count: counts.get(label) ?? 0,
      });
    }
    advanceBucket(start, granularity);
  }

  return buckets;
}

/**
 * Group certificates by a key, resolving human-readable names via the provided map.
 * Falls back to the raw ID when the name is not found.
 */
export function groupByKey(
  certificates: IssuanceCertificate[],
  key: "deviceGroupId" | "certificatePolicyId",
  nameMap: Map<string, string>
): GroupCount[] {
  const counts = new Map<string, number>();
  for (const cert of certificates) {
    const id = cert[key];
    counts.set(id, (counts.get(id) ?? 0) + 1);
  }

  const result: GroupCount[] = [];
  for (const [id, count] of counts) {
    result.push({
      id,
      name: nameMap.get(id) ?? id,
      count,
    });
  }
  return result;
}
